// News supports a deliberately small formatting subset. The previous contract
// placed the stored body directly into live `innerHTML`, which made a
// compromised admin account a stored-XSS primitive against every signed-in
// user. `renderNewsHtml()` parses into an inert template, then constructs a new
// DOM tree from an explicit tag/attribute allowlist. Raw nodes and attributes
// from the stored document are never adopted into the live page.

export const NEWS_TITLE_MAX_LENGTH = 160;
export const NEWS_BODY_MAX_LENGTH = 20_000;
export const NEWS_RENDER_MAX_DEPTH = 100;

const ALLOWED_TAGS = new Set([
  'a', 'b', 'blockquote', 'br', 'code', 'em', 'h2', 'h3', 'hr', 'i',
  'li', 'ol', 'p', 'pre', 's', 'span', 'strong', 'u', 'ul',
]);
// The block-level tags within ALLOWED_TAGS. Exported so callers that need to
// find block boundaries in a rendered body (news-modal.js) read this list
// instead of hand-copying it, which had let it drift out of sync.
export const NEWS_BLOCK_TAGS = new Set(['blockquote', 'h2', 'h3', 'hr', 'li', 'ol', 'p', 'pre', 'ul']);
const DROP_WITH_CONTENT = new Set([
  'embed', 'form', 'iframe', 'math', 'noscript', 'object', 'script', 'style',
  'svg', 'template',
]);
const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

function normalizeText(value, maxLength) {
  if (typeof value !== 'string' || value.includes('\0')) return null;
  const normalized = value.trim();
  if (!normalized || normalized.length > maxLength) return null;
  return normalized;
}

export function normalizeNewsTitle(value) {
  return normalizeText(value, NEWS_TITLE_MAX_LENGTH);
}

export function normalizeNewsBody(value) {
  return normalizeText(value, NEWS_BODY_MAX_LENGTH);
}

function safeHref(value, documentRef) {
  if (typeof value !== 'string' || !value || /[\u0000-\u001f\u007f]/.test(value)) return null;
  try {
    const documentBase = documentRef.baseURI;
    const base = documentBase && !documentBase.startsWith('about:')
      ? documentBase
      : 'https://retroplay.invalid/';
    const parsed = new URL(value, base);
    return SAFE_LINK_PROTOCOLS.has(parsed.protocol) ? value : null;
  } catch {
    return null;
  }
}

function appendAllowedChildren(target, source, documentRef, depth = 0) {
  if (depth >= NEWS_RENDER_MAX_DEPTH) return;

  for (const child of source.childNodes) {
    if (child.nodeType === 3) {
      target.appendChild(documentRef.createTextNode(child.textContent || ''));
      continue;
    }
    if (child.nodeType !== 1) continue;

    const tag = child.localName?.toLowerCase();
    if (!tag || DROP_WITH_CONTENT.has(tag)) continue;
    if (!ALLOWED_TAGS.has(tag)) {
      appendAllowedChildren(target, child, documentRef, depth + 1);
      continue;
    }

    const safeElement = documentRef.createElement(tag);
    if (tag === 'a') {
      const href = safeHref(child.getAttribute('href'), documentRef);
      if (href) safeElement.setAttribute('href', href);
      const title = child.getAttribute('title');
      if (title) safeElement.setAttribute('title', title.slice(0, 300));
    }
    appendAllowedChildren(safeElement, child, documentRef, depth + 1);
    target.appendChild(safeElement);
  }
}

export function renderNewsHtml(target, value) {
  if (!target?.ownerDocument) throw new TypeError('renderNewsHtml target must be a DOM element');
  const documentRef = target.ownerDocument;
  const template = documentRef.createElement('template');
  template.innerHTML = String(value ?? '');
  const safeFragment = documentRef.createDocumentFragment();
  appendAllowedChildren(safeFragment, template.content, documentRef);
  target.replaceChildren(safeFragment);
}
